import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  Toolbar,
  Chip,
  Avatar,
  Paper,
  InputAdornment,
  MenuItem,
  CircularProgress,
} from '@mui/material';

import {
  Person,
  FactCheck,
  Redeem,
  ShoppingBag,
  Cancel,
  Group,
  Search,
  Storefront,
  CalendarMonth,
  Payments,
} from '@mui/icons-material';

import axios from 'axios';
import { API } from '../../utils/Api';

const CustomerSupport = () => {
  const token = localStorage.getItem('token');

  const [mobileNumber, setMobileNumber] = useState('');
  const [customerData, setCustomerData] = useState(null);
  const [error, setError] = useState('');
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderFilter, setOrderFilter] = useState('ALL');

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
      case 'DELIVERED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      case 'CONFIRMED':
        return 'primary';
      case 'READY-TO-PICK':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPaymentChipColor = (status) => {
    if (status === 'captured') return 'success';
    if (status === 'refunded') return 'warning';
    if (status === 'failed') return 'error';
    if (status === 'created') return 'info';
    return 'default';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mobileNumber.length !== 10 || isNaN(mobileNumber)) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }

    setError('');
    setApiError('');
    setLoading(true);
    setCustomerData(null);

    try {
      const response = await axios.get(
        `${API.BASE_URL}user/getCustomerDetails?mobile=${mobileNumber}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCustomerData(response?.data);
    } catch (err) {
      console.error('Error fetching customer details:', err);
      setApiError(
        err?.response?.data?.data?.message ||
          err?.response?.data?.message ||
          'Unable to fetch customer details. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const orders = customerData?.orderStats?.orders || [];
  const coupons = customerData?.userAllocatedCoupons || [];
  const referrals = customerData?.referrals?.referredUsers || [];

  const filteredOrders = useMemo(() => {
    if (orderFilter === 'ALL') return orders;
    return orders.filter((order) => order.status === orderFilter);
  }, [orders, orderFilter]);

  const recentOrders = filteredOrders.slice(0, 15);

  const summaryCards = [
    {
      label: 'Total Orders',
      value: customerData?.orderStats?.totalOrders || 0,
      icon: <ShoppingBag />,
      color: '#1976d2',
      bg: '#e3f2fd',
    },
    {
      label: 'Cancelled Orders',
      value: customerData?.orderStats?.canceledOrders || 0,
      icon: <Cancel />,
      color: '#d32f2f',
      bg: '#ffebee',
    },
    {
      label: 'Referred Users',
      value: customerData?.referrals?.referredCount || 0,
      icon: <Group />,
      color: '#388e3c',
      bg: '#e8f5e9',
    },
    {
      label: 'Allocated Coupons',
      value: coupons.length,
      icon: <Redeem />,
      color: '#f57c00',
      bg: '#fff3e0',
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      <Toolbar />

      <Box sx={{ maxWidth: 1300, mx: 'auto' }}>
        <Typography
          variant="h4"
          sx={{
            mt: 3,
            mb: 1,
            fontWeight: 800,
            textAlign: 'center',
            color: '#003049',
          }}
        >
          Customer Support Dashboard
        </Typography>

        <Typography
          variant="body1"
          sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}
        >
          Search customer by mobile number and view complete support details.
        </Typography>

        <Paper
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: 2,
            mb: 4,
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            justifyContent: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
          }}
        >
          <TextField
            label="Mobile Number"
            variant="outlined"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            error={!!error}
            helperText={error}
            fullWidth
            sx={{ maxWidth: 360 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              minWidth: 150,
              height: 56,
              bgcolor: '#003049',
              fontWeight: 700,
              '&:hover': { bgcolor: '#002437' },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Search'}
          </Button>
        </Paper>

        {apiError && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#ffebee', color: '#c62828' }}>
            <Typography>{apiError}</Typography>
          </Paper>
        )}

        {customerData && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {summaryCards.map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                      height: '100%',
                    }}
                  >
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: item.bg,
                            color: item.color,
                            width: 52,
                            height: 52,
                          }}
                        >
                          {item.icon}
                        </Avatar>

                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 800 }}>
                            {item.value}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.label}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}>
                        <Person />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          Customer Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Basic profile and first outlet information
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Name
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }}>
                          {customerData?.customerDetails?.name || '-'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Mobile
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }}>
                          {customerData?.customerDetails?.mobile || '-'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                          {customerData?.customerDetails?.email || '-'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Joining Date
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }}>
                          {formatDate(customerData?.customerDetails?.joiningDate)}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <Storefront fontSize="small" color="primary" />
                            <Typography sx={{ fontWeight: 700 }}>First Ordered Outlet</Typography>
                          </Stack>

                          <Typography>
                            {customerData?.customerDetails?.firstOrderedOutlet?.name || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {customerData?.customerDetails?.firstOrderedOutlet?.address || '-'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {customerData?.customerDetails?.firstOrderedOutlet?.city || '-'}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={5}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: '#e8f5e9', color: '#388e3c' }}>
                        <FactCheck />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          Quick Facts
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Support-friendly customer summary
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Deleted Accounts</Typography>
                        <Chip
                          size="small"
                          label={customerData?.deletedAccounts?.length || 0}
                          color={
                            customerData?.deletedAccounts?.length > 0 ? 'error' : 'success'
                          }
                        />
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Joining Offer Used</Typography>
                        <Chip
                          size="small"
                          label={customerData?.couponDetails?.joiningOfferUsed ? 'Yes' : 'No'}
                          color={
                            customerData?.couponDetails?.joiningOfferUsed ? 'success' : 'default'
                          }
                        />
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Birthday Coupon Used</Typography>
                        <Chip
                          size="small"
                          label={customerData?.couponDetails?.birthdayCouponUsage ? 'Yes' : 'No'}
                          color={
                            customerData?.couponDetails?.birthdayCouponUsage
                              ? 'success'
                              : 'default'
                          }
                        />
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Referral Coupon Used</Typography>
                        <Chip
                          size="small"
                          label={customerData?.couponDetails?.referralCouponUsage ? 'Yes' : 'No'}
                          color={
                            customerData?.couponDetails?.referralCouponUsage
                              ? 'success'
                              : 'default'
                          }
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
                  <CardContent>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      spacing={2}
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          Recent Orders
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Showing latest 15 orders based on selected filter
                        </Typography>
                      </Box>

                      <TextField
                        select
                        size="small"
                        label="Filter Orders"
                        value={orderFilter}
                        onChange={(e) => setOrderFilter(e.target.value)}
                        sx={{ minWidth: 180 }}
                      >
                        <MenuItem value="ALL">All</MenuItem>
                        <MenuItem value="COMPLETED">Completed</MenuItem>
                        <MenuItem value="DELIVERED">Delivered</MenuItem>
                        <MenuItem value="CANCELLED">Cancelled</MenuItem>
                        <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                        <MenuItem value="READY-TO-PICK">Ready To Pick</MenuItem>
                      </TextField>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={1.5}>
                      {recentOrders.length > 0 ? (
                        recentOrders.map((order) => {
                          const refund =
                            order?.orderCancellationRequestInfo?.refundResponse || null;

                          return (
                            <Paper
                              key={order.orderId}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid #edf0f2',
                                bgcolor: '#fff',
                              }}
                            >
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Order ID
                                  </Typography>
                                  <Typography sx={{ fontWeight: 700 }}>
                                    #{order.orderId?.slice(-8)}
                                  </Typography>
                                </Grid>

                                <Grid item xs={6} md={2}>
                                  <Typography variant="caption" color="text.secondary">
                                    Status
                                  </Typography>
                                  <Box>
                                    <Chip
                                      label={order.status || '-'}
                                      color={getStatusColor(order.status)}
                                      size="small"
                                    />
                                  </Box>
                                </Grid>

                                <Grid item xs={6} md={2}>
                                  <Typography variant="caption" color="text.secondary">
                                    Payment
                                  </Typography>
                                  <Box>
                                    <Chip
                                      label={order.paymentStatus?.status || 'N/A'}
                                      color={getPaymentChipColor(order.paymentStatus?.status)}
                                      size="small"
                                      icon={<Payments />}
                                    />
                                  </Box>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                  <Typography variant="caption" color="text.secondary">
                                    Outlet
                                  </Typography>
                                  <Typography sx={{ fontWeight: 600 }}>
                                    {order.outlet?.name || '-'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {order.outlet?.city || '-'}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} md={2}>
                                  <Typography variant="caption" color="text.secondary">
                                    Created
                                  </Typography>
                                  <Typography sx={{ fontWeight: 600 }}>
                                    {formatDateTime(order.createdAt)}
                                  </Typography>
                                </Grid>

                                {refund && (
                                  <Grid item xs={12}>
                                    <Paper
                                      sx={{
                                        p: 1.5,
                                        bgcolor: '#fff8e1',
                                        borderRadius: 2,
                                        mt: 1,
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        Refund Details
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary">
                                        Refund ID: {refund.refund_id || refund.id || '-'} | Status:{' '}
                                        {refund.status || '-'} | Amount: ₹
                                        {refund.amount ? refund.amount / 100 : 0}
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                )}
                              </Grid>
                            </Paper>
                          );
                        })
                      ) : (
                        <Typography color="text.secondary">No orders found.</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: '#e8f5e9', color: '#388e3c' }}>
                        <Group />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          Referred Users
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Users referred by this customer
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={1.5}>
                      {referrals.length > 0 ? (
                        referrals.map((user, index) => (
                          <Paper
                            key={`${user.email}-${index}`}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid #edf0f2',
                            }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Avatar>
                                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                              </Avatar>

                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                                  {user.name || '-'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ wordBreak: 'break-word' }}
                                >
                                  {user.email || '-'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Joined: {formatDate(user.joinedAt)}
                                </Typography>
                              </Box>

                              <Chip
                                size="small"
                                label={user.isFirstPurchase ? 'Purchased' : 'No Purchase'}
                                color={user.isFirstPurchase ? 'success' : 'default'}
                              />
                            </Stack>
                          </Paper>
                        ))
                      ) : (
                        <Typography color="text.secondary">No referred users found.</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: '#fff3e0', color: '#f57c00' }}>
                        <Redeem />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          Allocated Coupons
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Coupons currently allocated to customer
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={1.5}>
                      {coupons.length > 0 ? (
                        coupons.map((coupon) => (
                          <Paper
                            key={coupon._id}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid #edf0f2',
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              spacing={2}
                            >
                              <Box>
                                <Typography sx={{ fontWeight: 800 }}>
                                  {coupon.title || '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {coupon.description || '-'}
                                </Typography>
                              </Box>

                              <Chip
                                size="small"
                                label={coupon.status || '-'}
                                color={coupon.status === 'ACTIVE' ? 'success' : 'default'}
                              />
                            </Stack>

                            <Stack spacing={1} sx={{ mt: 1.5 }}>
                              <Typography variant="body2">
                                <b>Code:</b> {coupon.couponCode || '-'}
                              </Typography>

                              <Typography variant="body2">
                                <b>Discount:</b>{' '}
                                {coupon.discountPercent
                                  ? `${coupon.discountPercent}%`
                                  : coupon.discountAmount
                                  ? `₹${coupon.discountAmount}`
                                  : '-'}
                              </Typography>

                              <Typography variant="body2">
                                <b>Minimum Order:</b> ₹{coupon.minOrderAmount || 0}
                              </Typography>

                              <Stack direction="row" spacing={1} alignItems="center">
                                <CalendarMonth fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(coupon.validityStartDate)} -{' '}
                                  {formatDate(coupon.validityEndDate)}
                                </Typography>
                              </Stack>

                              {coupon.termsAndConditions?.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                    Terms
                                  </Typography>
                                  {coupon.termsAndConditions.slice(0, 3).map((term, index) => (
                                    <Typography
                                      key={index}
                                      variant="caption"
                                      display="block"
                                      color="text.secondary"
                                    >
                                      • {term}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </Stack>
                          </Paper>
                        ))
                      ) : (
                        <Typography color="text.secondary">No coupons allocated.</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default CustomerSupport;